import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined'
import { Alert, Button, Stack } from '@mui/material'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NewCateringTeamOrderDialog } from './new-catering-team-order-dialog'
import { CartToolbarButton } from './toolbar-button'

type Props = {
  onNewDraft: () => void
  onNewCateringTeamOrder: () => void
  disabled: boolean
  showCateringTeamOrder: boolean
  /** When set (single-basket mode with a live draft), creation asks before replacing it. */
  confirmationText?: string
}

const CartToolbar: React.FC<Props> = ({
  onNewDraft,
  onNewCateringTeamOrder,
  disabled,
  showCateringTeamOrder,
  confirmationText,
}) => {
  // New draft confirms inline; New team order always explains itself in a dialog first, and
  // that dialog carries the replace-draft warning so there is never a second step.
  const [draftPending, setDraftPending] = useState(false)
  const [cateringTeamOrderDialogOpen, setCateringTeamOrderDialogOpen] = useState(false)
  const { t } = useTranslation()

  const handleDraftClick = () => {
    if (confirmationText) setDraftPending(true)
    else onNewDraft()
  }

  return (
    <Stack alignSelf="flex-end" alignItems="flex-end" spacing={1} paddingBottom={1}>
      {/* Wraps onto two lines rather than clipping: DE/NL/IT labels don't fit the basket column side by side. */}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
        <CartToolbarButton
          label={t('marketplace:newDraft')}
          icon={<AddCircleOutline />}
          disabled={draftPending || disabled}
          onClick={handleDraftClick}
        />
        {showCateringTeamOrder && (
          <CartToolbarButton
            label={t('marketplace:newCateringTeamOrder')}
            icon={<GroupAddOutlined />}
            disabled={draftPending || disabled}
            onClick={() => setCateringTeamOrderDialogOpen(true)}
          />
        )}
      </Stack>
      {showCateringTeamOrder && (
        <NewCateringTeamOrderDialog
          open={cateringTeamOrderDialogOpen}
          confirmationText={confirmationText}
          onCancel={() => setCateringTeamOrderDialogOpen(false)}
          onConfirm={() => {
            setCateringTeamOrderDialogOpen(false)
            onNewCateringTeamOrder()
          }}
        />
      )}
      {draftPending ? (
        <Alert severity="warning" variant="outlined">
          <Stack direction="column">
            {confirmationText}
            <Stack width="100%" direction="row" justifyContent="flex-end" spacing={0.5}>
              <Button onClick={() => setDraftPending(false)} size="small" variant="contained">
                {t('marketplace:keepCurrent')}
              </Button>
              <Button
                onClick={() => {
                  setDraftPending(false)
                  onNewDraft()
                }}
                size="small"
                variant="outlined">
                {t('marketplace:createNew')}
              </Button>
            </Stack>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  )
}

export default CartToolbar
